import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MessageSquare, Plus, Settings } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import {
  nextgenConversationPath,
  nextgenProjectPath,
  nextgenProjectSettingsPath,
} from "~/pages/manifold-nextgen/paths";
import {
  deleteConversation as apiDeleteConversation,
  updateConversation as apiUpdateConversation,
} from "~/services/conversations";
import { ProjectConversation, projectConversationsPath } from "~/services/projects";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";

export interface ProjectSidebarProps {
  projectId: string;
  sessions: ProjectConversation[] | undefined;
}

interface SessionRowProps {
  projectId: string;
  session: ProjectConversation;
  isRenaming: boolean;
  onStartRename: () => void;
  onEndRename: () => void;
  onShare: () => void;
  onDelete: () => void;
}

const SessionRow = ({
  projectId,
  session,
  isRenaming,
  onStartRename,
  onEndRename,
  onShare,
  onDelete,
}: SessionRowProps) => {
  const [draft, setDraft] = useState(session.display_name);
  const inputRef = useRef<HTMLInputElement>(null);

  const [flashing, setFlashing] = useState(false);
  const prevNameRef = useRef(session.display_name);

  useEffect(() => {
    if (prevNameRef.current === session.display_name) {
      return;
    }
    prevNameRef.current = session.display_name;
    setFlashing(true);
    const id = window.setTimeout(() => setFlashing(false), 900);
    return () => window.clearTimeout(id);
  }, [session.display_name]);

  useEffect(() => {
    if (isRenaming) {
      setDraft(session.display_name);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isRenaming, session.display_name]);

  const commit = () => {
    const next = draft.trim();
    const previous = session.display_name;
    onEndRename();
    if (!next || next === previous) {
      return;
    }
    const key = projectConversationsPath(projectId);
    const patch = (display_name: string) =>
      mutate(
        key,
        (current: { items: ProjectConversation[] } | undefined) =>
          current && {
            ...current,
            items: current.items.map((s) => (s.id === session.id ? { ...s, display_name } : s)),
          },
        { revalidate: false }
      );
    void patch(next);
    void apiUpdateConversation(session.id, { display_name: next }).catch(() => {
      void patch(previous);
      void mutate(key);
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  return (
    <li className="group relative cursor-pointer">
      <NavLink
        to={nextgenConversationPath(projectId, session.id)}
        className={({ isActive }) =>
          cn(
            "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5",
            isActive
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
          )
        }
        onClick={(e) => {
          if (isRenaming) {
            e.preventDefault();
          }
        }}
      >
        {session.agent ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">
                <AgentAvatar id={session.agent.id} name={session.agent.name} size={18} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{session.agent.display_name}</TooltipContent>
          </Tooltip>
        ) : (
          <div
            className="flex size-[18px] shrink-0 items-center justify-center text-current"
            aria-hidden="true"
          >
            <MessageSquare className="size-3" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.preventDefault()}
              aria-label="Rename session"
              className="w-full rounded border border-border bg-background px-1 py-0 text-xs font-medium text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span
              className={cn(
                "block truncate pr-4 text-sm font-medium transition-colors duration-700",
                flashing && "text-primary"
              )}
            >
              {session.display_name}
            </span>
          )}
        </div>
      </NavLink>

      {!isRenaming && (
        <ConversationActionsMenu onShare={onShare} onRename={onStartRename} onDelete={onDelete} />
      )}
    </li>
  );
};

interface ActionTarget {
  session: ProjectConversation;
  action: "share" | "delete";
}

const RecentSessionsList = ({
  projectId,
  sessions,
}: {
  projectId: string;
  sessions: ProjectConversation[];
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);

  const handleDelete = async () => {
    if (!actionTarget) {
      return;
    }
    const { session } = actionTarget;
    await apiDeleteConversation(session.id);
    await mutate(projectConversationsPath(projectId));
    if (location.pathname.includes(`/c/${session.id}`)) {
      navigate(nextgenProjectPath(projectId));
    }
  };

  return (
    <>
      <ul className="flex flex-col gap-0.5" role="list">
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            projectId={projectId}
            session={session}
            isRenaming={renamingId === session.id}
            onStartRename={() => setRenamingId(session.id)}
            onEndRename={() => setRenamingId(null)}
            onShare={() => setActionTarget({ session, action: "share" })}
            onDelete={() => setActionTarget({ session, action: "delete" })}
          />
        ))}
      </ul>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationId={actionTarget?.session.id ?? ""}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationTitle={actionTarget?.session.display_name ?? ""}
        onConfirm={handleDelete}
      />
    </>
  );
};

export const ProjectSidebar = ({ projectId, sessions }: ProjectSidebarProps) => {
  const navigate = useNavigate();

  return (
    <>
      <nav
        aria-label="Project navigation"
        className="flex w-56 shrink-0 flex-col border-r border-border"
      >
        <div className="px-4 pb-3 pt-4">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-primary hover:text-primary/80"
            onClick={() => navigate(nextgenProjectPath(projectId))}
          >
            <Plus className="size-4" />
            New Session
          </Button>
        </div>

        <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-2">
          <p className="py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Recent sessions
          </p>
          {sessions === undefined ? (
            <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="flex items-center gap-2 py-1.5">
                  <Skeleton className="size-[18px] shrink-0 rounded-full" />
                  <Skeleton className="h-3 w-28" />
                </li>
              ))}
            </ul>
          ) : sessions.length === 0 ? (
            <p className="py-1 text-xs italic text-muted-foreground/80">No sessions yet.</p>
          ) : (
            <RecentSessionsList projectId={projectId} sessions={sessions} />
          )}
        </div>

        <div className="border-t border-border px-4 py-3">
          <NavLink
            to={nextgenProjectSettingsPath(projectId)}
            aria-label="Project settings"
            className={({ isActive }) =>
              cn(
                "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium",
                isActive
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )
            }
          >
            <Settings className="size-4" />
            Settings
          </NavLink>
        </div>
      </nav>
    </>
  );
};
